<template>
  <div class="main-layout" data-ds="wangshu">
    <!-- 顶部导航栏 -->
    <header class="top-navbar">
      <div class="navbar-left">
        <!-- Logo -->
        <div class="logo-container">
          <div class="logo-icon">
            <img src="/logo.png" alt="Wangshu AI Logo" class="logo-image" />
          </div>
          <div class="logo-text">
            <div class="logo-title">{{ PLATFORM_NAME }}</div>
            <div class="logo-subtitle"></div>
          </div>
        </div>
        
        <!-- 应用中心 -->
        <a-trigger
          v-if="showHorizontalMenu"
          trigger="click"
          position="bl"
          popup-container="body"
          content-class="app-launcher-trigger-content"
          :popup-style="{ zIndex: 2600 }"
          :popup-translate="[0, 8]"
          :auto-fit-position="true"
          :update-at-scroll="true"
          v-model:popup-visible="appMenuVisible"
        >
          <button
            class="app-launcher-btn"
            :class="{ 'app-launcher-btn--active': appMenuVisible }"
            type="button"
            aria-label="打开应用中心"
            :aria-expanded="appMenuVisible"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="1" width="6.5" height="6.5" rx="2" fill="currentColor" opacity="0.9"/>
              <rect x="10.5" y="1" width="6.5" height="6.5" rx="2" fill="currentColor" opacity="0.6"/>
              <rect x="1" y="10.5" width="6.5" height="6.5" rx="2" fill="currentColor" opacity="0.6"/>
              <rect x="10.5" y="10.5" width="6.5" height="6.5" rx="2" fill="currentColor" opacity="0.4"/>
            </svg>
          </button>
          <template #content>
            <div class="app-launcher-popup" data-ds="wangshu">
              <div class="app-launcher-header">
                <div class="app-launcher-heading">
                  <span class="app-launcher-title">应用中心</span>
                  <span class="app-launcher-subtitle">搜索页面、产品域和控制台入口</span>
                </div>
                <a-input-search
                  v-model="appLauncherSearch"
                  placeholder="搜索入口..."
                  allow-clear
                  size="small"
                  class="app-launcher-search"
                />
              </div>
              <div class="app-launcher-grid">
                <div 
                  v-for="app in appMenuItems" 
                  :key="app.key"
                  class="app-launcher-card"
                  :class="{
                    'app-launcher-card--active': currentAppKey === app.key,
                    'app-launcher-card--selected': selectedAppKey === app.key,
                  }"
                  role="button"
                  tabindex="0"
                  @click="handleAppMenuSelect(app)"
                  @keydown.enter.prevent="handleAppMenuSelect(app)"
                  @keydown.space.prevent="handleAppMenuSelect(app)"
                >
                  <div class="app-card-icon">
                    <component :is="app.icon" :size="22" />
                  </div>
                  <div class="app-card-info">
                    <span class="app-card-name">{{ app.label }}</span>
                    <span class="app-card-desc">{{ app.description }}</span>
                  </div>
                  <div v-if="currentAppKey === app.key" class="app-card-active-dot" />
                </div>
              </div>
              <div v-if="selectedAppMenuItems.length > 0" class="app-launcher-submenu">
                <div class="app-submenu-header">
                  <span>{{ selectedAppLabel }}</span>
                  <button
                    type="button"
                    class="app-submenu-default"
                    @click.stop="handleSelectedAppDefaultRoute"
                  >
                    进入默认页
                  </button>
                </div>
                <div class="app-submenu-list">
                  <button
                    v-for="item in filteredSelectedAppMenuItems"
                    :key="item.key"
                    class="app-submenu-item"
                    type="button"
                    @mousedown.stop
                    @click.stop="handleAppMenuItemSelect(item)"
                    @keydown.enter.stop.prevent="handleAppMenuItemSelect(item)"
                    @keydown.space.stop.prevent="handleAppMenuItemSelect(item)"
                  >
                    <span class="app-submenu-title">{{ item.title }}</span>
                    <span class="app-submenu-breadcrumb">{{ item.breadcrumb }}</span>
                  </button>
                  <div v-if="filteredSelectedAppMenuItems.length === 0" class="app-submenu-empty">
                    没有匹配入口
                  </div>
                </div>
              </div>
            </div>
          </template>
        </a-trigger>
        
        <!-- 项目切换下拉框 - 放在九宫格右侧 -->
        <!-- 「管理与运维」域为企业级/平台级视图（权限/项目/集群运维均跨项目），项目切换对其无意义 → 隐藏 -->
        <a-trigger v-if="!isAdminDomain" trigger="click" position="bl" :popup-translate="[0, 4]" v-model:popup-visible="projectDropdownVisible">
          <div class="project-selector" role="button" tabindex="0">
            <img src="/logo.png" alt="" class="project-icon" />
            <span class="project-name">{{ currentProject?.project_name || '选择项目' }}</span>
            <icon-down :size="12" />
          </div>
          <template #content>
            <div class="project-dropdown-panel" data-ds="wangshu">
              <div class="project-dropdown-header">切换项目</div>
              <div class="project-dropdown-list">
                <div
                  v-for="project in projectList.filter(p => p.is_active)"
                  :key="project.project_id"
                  class="project-dropdown-item"
                  :class="{
                    'project-option-active': project.project_id === currentProjectId,
                  }"
                  role="button"
                  tabindex="0"
                  @click="handleProjectChange(project)"
                  @keydown.enter.prevent="handleProjectChange(project)"
                  @keydown.space.prevent="handleProjectChange(project)"
                >
                  <img src="/logo.png" alt="" class="project-option-icon" />
                  <span class="project-option-name">{{ project.project_name || project.project_id }}</span>
                  <icon-check v-if="project.project_id === currentProjectId" class="project-check" />
                </div>
              </div>
            </div>
          </template>
        </a-trigger>
      </div>

      <!-- 右侧工具 -->
      <div class="navbar-right">
        <!-- 智能体实时监控入口（顶部导航栏版本） -->
        <div
          class="navbar-agent-widget"
          role="button"
          tabindex="0"
          aria-label="打开智能体实时监控"
          @click="handleAgentClick"
          @keydown.enter.prevent="handleAgentClick"
          @keydown.space.prevent="handleAgentClick"
        >
          <div class="navbar-robot">
            <div class="navbar-robot-antenna"></div>
            <div class="navbar-robot-ear ear-left"></div>
            <div class="navbar-robot-ear ear-right"></div>
            <div class="navbar-robot-face">
              <div class="navbar-robot-eye"></div>
              <div class="navbar-robot-eye"></div>
            </div>
          </div>
        </div>

        <!-- 全局命令触发按钮 -->
        <button
          class="command-trigger-btn"
          type="button"
          aria-label="呼叫智能体"
          @click="openCommandModal"
        >
          <span class="trigger-text">呼叫智能体...</span>
          <span class="trigger-shortcut">
            <span class="shortcut-key">{{ isMac ? '⌘' : 'Ctrl' }}</span>
            <span class="shortcut-key">K</span>
          </span>
        </button>
        <UserDropdownMenu @logout="onUserLogout" />
      </div>
    </header>

    <!-- 全局异常告警横幅 -->
    <GlobalAlertBanner />

    <!-- 主内容区域（双轨制侧边栏） -->
    <div class="content-wrapper">
      <!-- 平台菜单侧边栏（历史组件，当前默认隐藏） -->
      <GovernanceSidebar v-if="isProductPage" />
      <!-- AI 全域洞察等页面的侧边栏 -->
      <SlimSidebar v-else-if="showSidebar" />
      <main
        class="main-content"
        :class="{ 'with-sidebar': showSidebar || isProductPage }"
        data-ds="wangshu"
      >
        <ErrorBoundary>
          <router-view />
        </ErrorBoundary>
      </main>
    </div>

    <!-- 全局命令弹窗 -->
    <GlobalCommandModal
      v-model="commandModalVisible"
      :user-name="currentUserName"
      :loading="commandLoading"
    />

    <!-- Agent Commander 聚光灯弹窗 -->
    <AgentCommander
      v-model="agentCommanderVisible"
      @select="handleAgentSelect"
    />

  </div>
</template>

<script setup lang="ts">
import AgentCommander from '@/components/common/AgentCommander.vue'
import ErrorBoundary from '@/components/common/ErrorBoundary.vue'
import GlobalAlertBanner from '@/components/layout/GlobalAlertBanner.vue'
import GlobalCommandModal from '@/components/common/GlobalCommandModal.vue'
import UserDropdownMenu from '@/components/common/UserDropdownMenu.vue'
import {
  DEFAULT_ROUTE,
  HIDDEN_NAV_ROUTE_PREFIXES,
  KNOWLEDGE_ROUTE_PREFIX,
  AGENT_DEFAULT_ROUTE, AGENT_ROUTE_PREFIX,
  MISSION_DEFAULT_ROUTE, MISSION_ROUTE_PREFIX,
  CONTROL_DEFAULT_ROUTE, CONTROL_ROUTE_PREFIX,
  isProductRoute,
} from '@/constants/routeConfig'
import { PLATFORM_NAME } from '@/constants/branding'
import { PRODUCT_DOMAINS, findDomainByRoute, type MenuItem } from '@/config/menuConfig'
import { filterMenuTreeByAccess } from '@/router/permissions'
import { useCommandCenterStore } from '@/stores/commandCenter'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { getProjectId, setProjectId } from '@/utils'
import { Message } from '@arco-design/web-vue'
import IconBook from '@arco-design/web-vue/es/icon/icon-book'
import IconCheck from '@arco-design/web-vue/es/icon/icon-check'
import IconDashboard from '@arco-design/web-vue/es/icon/icon-dashboard'
import IconDown from '@arco-design/web-vue/es/icon/icon-down'
import IconRobot from '@arco-design/web-vue/es/icon/icon-robot'
import IconSettings from '@arco-design/web-vue/es/icon/icon-settings'
import IconStorage from '@arco-design/web-vue/es/icon/icon-storage'
import IconThunderbolt from '@arco-design/web-vue/es/icon/icon-thunderbolt'
import { computed, markRaw, onErrorCaptured, onMounted, onUnmounted, ref, watch, type Component } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import GovernanceSidebar from './GovernanceSidebar.vue'
import SlimSidebar from './SlimSidebar.vue'

const route = useRoute()
const router = useRouter()
const commandCenterStore = useCommandCenterStore()
const authStore = useAuthStore()
const themeStore = useThemeStore()

// MainLayout 接管渲染时再恢复 Claude 系主题，避免从工作台跳回时旧页面提前换肤。
themeStore.ensureClaudeTheme()

// UserDropdownMenu 触发登出前先关闭 MainLayout 自有弹层，避免 a-trigger 在 store 清空时报 null
function onUserLogout(): void {
  appMenuVisible.value = false
  projectDropdownVisible.value = false
  agentCommanderVisible.value = false
  commandModalVisible.value = false
}

// ========== 应用中心相关 ==========
interface AppMenuItem {
  key: string
  label: string
  description: string
  icon: Component
  route?: string
  routePrefix?: string
}

interface AppSubmenuItem {
  key: string
  title: string
  breadcrumb: string
  route: string
}

const allAppMenuItems: AppMenuItem[] = [
  { key: 'agent-workspace', label: '智能体工作台', description: '会话、任务、工具调用',
    icon: markRaw(IconRobot),
    route: '/agents/workspace', routePrefix: '/agents/workspace' },
  { key: 'workbench', label: '业务工作台', description: '业务任务、召回、增长运营',
    icon: markRaw(IconDashboard),
    route: '/workbench/document-search', routePrefix: '/workbench' },
  { key: 'data', label: '数据平台', description: '目录、接入、开发、治理',
    icon: markRaw(IconStorage),
    route: '/knowledge/datalake/unified-catalog', routePrefix: KNOWLEDGE_ROUTE_PREFIX },
  { key: 'agent', label: '智能体工程', description: '智能体、知识、模型、评测',
    icon: markRaw(IconBook),
    route: AGENT_DEFAULT_ROUTE, routePrefix: AGENT_ROUTE_PREFIX },
  { key: 'operations', label: '自动化运营', description: '执行监控、调度、人机协同',
    icon: markRaw(IconThunderbolt),
    route: MISSION_DEFAULT_ROUTE, routePrefix: MISSION_ROUTE_PREFIX },
  { key: 'admin', label: '管理与运维', description: '权限、项目、组件、系统运维',
    icon: markRaw(IconSettings),
    route: CONTROL_DEFAULT_ROUTE, routePrefix: CONTROL_ROUTE_PREFIX },
]

const appMenuItems = computed(() => {
  return allAppMenuItems.filter((item) => {
    const domain = PRODUCT_DOMAINS.find((candidate) => candidate.key === item.key)
    if (!domain) return true
    return filterMenuTreeByAccess(domain.menu, authStore.canAccessRoute).length > 0
  })
})

// 当前选中的应用（通过 meta.menuGroup + 菜单路由反查所属域）
const currentAppKey = computed(() => {
  // 优先用 meta.menuGroup + 路径命中九域
  const menuGroup = (route.meta?.menuGroup as string | undefined) ?? undefined
  const domain = findDomainByRoute(route.path, menuGroup)
  if (domain) return domain.key
  // 降级：旧前缀匹配
  for (const item of appMenuItems.value) {
    const prefix = item.routePrefix ?? item.route
    if (prefix && (route.path === prefix || route.path.startsWith(prefix + '/'))) {
      return item.key
    }
  }
  return ''
})

// 「管理与运维」域为企业级/平台级视图（权限/项目/集群运维/系统监控均跨项目），
// 项目切换对其页面数据无影响 → 隐藏左上角项目下拉，避免「切了没反应」的误导。
// 该域内的混合页面（组件/SQL 草稿审核）有各自页面内的 scope 选择器，不依赖此全局下拉。
const isAdminDomain = computed(() => currentAppKey.value === 'admin')

// 应用菜单弹出状态
const appMenuVisible = ref(false)
const selectedAppKey = ref('')
const appLauncherSearch = ref('')

watch(
  appMenuVisible,
  (visible) => {
    if (visible) {
      selectedAppKey.value = currentAppKey.value || appMenuItems.value[0]?.key || ''
      appLauncherSearch.value = ''
    }
  },
)

// 项目下拉弹出状态（打开时刷新列表，确保 is_active 是最新状态）
const projectDropdownVisible = ref(false)
watch(projectDropdownVisible, (visible) => {
  if (visible) {
    loadProjectList()
  }
})

// 处理应用菜单选择（使用 a-trigger + 原生 div 点击，避免 a-doption 事件吞没问题）
function handleAppMenuSelect(app: AppMenuItem) {
  selectedAppKey.value = app.key
}

const selectedAppLabel = computed(() => {
  return allAppMenuItems.find((item) => item.key === selectedAppKey.value)?.label ?? ''
})

const selectedAppMenuItems = computed<AppSubmenuItem[]>(() => {
  const domain = PRODUCT_DOMAINS.find((candidate) => candidate.key === selectedAppKey.value)
  if (!domain) return []
  return flattenMenuLeaves(filterMenuTreeByAccess(domain.menu, authStore.canAccessRoute))
})

const filteredSelectedAppMenuItems = computed<AppSubmenuItem[]>(() => {
  const keyword = appLauncherSearch.value.trim().toLowerCase()
  if (!keyword) {
    return selectedAppMenuItems.value
  }
  return selectedAppMenuItems.value.filter((item) => {
    const haystack = `${item.title} ${item.breadcrumb}`.toLowerCase()
    return haystack.includes(keyword)
  })
})

function flattenMenuLeaves(items: MenuItem[], ancestors: string[] = []): AppSubmenuItem[] {
  const leaves: AppSubmenuItem[] = []
  for (const item of items) {
    const breadcrumb = [...ancestors, item.title]
    if (item.route) {
      leaves.push({
        key: item.key,
        title: item.title,
        breadcrumb: breadcrumb.slice(0, -1).join(' / '),
        route: item.route,
      })
    }
    if (item.children?.length) {
      leaves.push(...flattenMenuLeaves(item.children, breadcrumb))
    }
  }
  return leaves
}

function handleAppMenuItemSelect(item: AppSubmenuItem): void {
  appMenuVisible.value = false
  void router.push(item.route)
}

function handleSelectedAppDefaultRoute(): void {
  const app = allAppMenuItems.find((item) => item.key === selectedAppKey.value)
  if (!app?.route) return
  appMenuVisible.value = false
  void router.push(app.route)
}

// ========== 项目切换相关 ==========
import { fetchProjectSelector, type ProjectSelectorItem } from '@/api/projects'
import { logger } from '@/utils/logger'

const projectList = ref<ProjectSelectorItem[]>([])

const currentProjectId = ref<string>('')

const currentProject = computed(() => {
  return projectList.value.find(p => p.project_id === currentProjectId.value)
})

async function loadProjectList() {
  try {
    const res = await fetchProjectSelector()
    projectList.value = res.items
  } catch (e) {
    logger.error('[MainLayout] 加载项目列表失败:', e)
  }
}

function isProjectActive(projectId: string): boolean {
  const project = projectList.value.find(p => p.project_id === projectId)
  return !!project?.is_active
}

function selectFirstActiveProject(): void {
  const firstActive = projectList.value.find(p => p.is_active)
  if (firstActive) {
    currentProjectId.value = firstActive.project_id
    setProjectId(firstActive.project_id)
  }
}

function initProjectId() {
  const urlParams = new URLSearchParams(window.location.search)
  const urlProjectId = urlParams.get('projectId') || urlParams.get('project_id')
  if (urlProjectId && isProjectActive(urlProjectId)) {
    currentProjectId.value = urlProjectId
    setProjectId(urlProjectId)
    return
  }

  const storedProjectId = getProjectId()
  if (storedProjectId && isProjectActive(storedProjectId)) {
    currentProjectId.value = storedProjectId
    return
  }

  selectFirstActiveProject()
}

function handleProjectChange(project: ProjectSelectorItem) {
  if (project.project_id === currentProjectId.value) return

  projectDropdownVisible.value = false
  currentProjectId.value = project.project_id
  setProjectId(project.project_id)
  void authStore.loadPermissions()

  Message.success(`已切换到项目: ${project.project_name || project.project_id}`)

  window.location.reload()
}

// 全局命令弹窗状态
const commandModalVisible = ref(false)
const currentUserName = computed(() => commandCenterStore.currentUser)
const commandLoading = computed(() => commandCenterStore.loading)

// Agent Commander 弹窗状态
const agentCommanderVisible = ref(false)

// 检测是否为 Mac 系统
const isMac = ref(false)

// 检测操作系统
function detectOS() {
  isMac.value = /Mac|iPhone|iPod|iPad/i.test(navigator.platform) || 
                /Mac|iPhone|iPod|iPad/i.test(navigator.userAgent)
}

// 打开命令弹窗
function openCommandModal() {
  // 打开 Agent Commander 而不是原来的命令弹窗
  agentCommanderVisible.value = true
}

// 打开 Agent Commander
function openAgentCommander() {
  agentCommanderVisible.value = true
}

// Agent 信息类型（与 AgentCommander 组件的 AgentEmployee 对齐）
interface AgentInfo {
  key: string
  name: string
  avatarText: string
  title: string
  skills: Array<{ icon: string; text: string }>
  status: 'online' | 'busy' | 'offline'
  statusText: string
  color: 'blue' | 'cyan' | 'green' | 'purple' | 'pink'
  route?: string
}

// 处理智能体选择（路由跳转由 AgentCommander 内部完成，此处仅作扩展点）
function handleAgentSelect(_agent: AgentInfo) {
  // AgentCommander.handleSelectAgent 已自行 router.push，无需重复导航
}

// 关闭命令弹窗
function closeCommandModal() {
  commandModalVisible.value = false
}

// 快捷键监听
function handleKeyDown(event: KeyboardEvent) {
  // 检查是否按下了 Cmd+K (Mac) 或 Ctrl+K (Windows/Linux)
  const isCommandKey = isMac.value ? event.metaKey : event.ctrlKey
  const isKKey = event.key === 'k' || event.key === 'K'
  
  // 如果正在输入框中，不触发（避免与输入冲突）
  const target = event.target as HTMLElement
  if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
    return
  }
  
  if (isCommandKey && isKKey) {
    event.preventDefault()
    event.stopPropagation()
    if (agentCommanderVisible.value) {
      agentCommanderVisible.value = false
    } else {
      openAgentCommander()
    }
  }
  
  // ESC 键关闭弹窗
  if (event.key === 'Escape') {
    if (commandModalVisible.value) {
      closeCommandModal()
    }
    if (agentCommanderVisible.value) {
      agentCommanderVisible.value = false
    }
  }

  // Ctrl+Shift+C → 打开控制台
  if (isCommandKey && event.shiftKey && (event.key === 'c' || event.key === 'C')) {
    event.preventDefault()
    router.push('/console')
  }
}

// 点击 Agent 机器人（打开 Agent Commander）
function handleAgentClick() {
  router.push(DEFAULT_ROUTE)
}



// 捕获子组件渲染/生命周期错误，防止 a-trigger 等弹出组件的卸载异常
// 导致整个布局崩溃（典型场景：九宫格 popup 在 teleport 清理时报 null 引用）
onErrorCaptured((err, _instance, info) => {
  logger.warn('[MainLayout] 捕获子组件错误:', info, err)
  return false
})

onMounted(async () => {
  detectOS()
  await loadProjectList()
  initProjectId()
  window.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  // 卸载前关闭所有弹出层，防止 teleport 内容残留导致 null 引用
  appMenuVisible.value = false
  projectDropdownVisible.value = false
  agentCommanderVisible.value = false
  commandModalVisible.value = false
  window.removeEventListener('keydown', handleKeyDown)
})

const isProductPage = computed(() => {
  if (route.meta?.hideSidebar) return false
  return isProductRoute(route.path)
})

// 根据路由决定是否显示横向菜单（顶部导航栏）
// 只在登录、注册等页面隐藏
const showHorizontalMenu = computed(() => {
  return !HIDDEN_NAV_ROUTE_PREFIXES.some(r => route.path.startsWith(r))
})

// 根据路由决定是否显示侧边栏
// 当前禁用侧边栏显示，顶部菜单负责六域切换
const showSidebar = computed(() => {
  // 暂时禁用 SlimSidebar，只使用 GovernanceSidebar
  return false
})

// 侧边栏菜单配置已移至 SlimSidebar 组件，使用全局 DATAMANUS_MENU
</script>

<style scoped lang="scss">
@use '@/styles/variables' as *;

.main-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

.top-navbar {
  // Claude 素色：顶栏跟整页底色 canvas 同色，仅靠底部 1px 分隔线区分
  height: 56px;
  background: var(--c-bg-canvas);
  border-bottom: 1px solid var(--c-border-1);
  display: flex;
  align-items: center;
  padding: 0 20px;
  justify-content: space-between;
  flex-shrink: 0;
  position: relative;
  z-index: 1001;
  // 移除 backdrop-filter：素色风不需要毛玻璃
}

.navbar-left {
  display: flex;
  align-items: center;
  gap: 0;
  min-width: 0;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-right: 16px;
  margin-right: 4px;
  border-right: 1px solid var(--hairline);
}

.logo-icon {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  // Claude 素色：彩色 logo 用 filter 单色化（grayscale + 调暗）
  filter: grayscale(1) brightness(0.4) contrast(1.4);
}

.logo-text {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.logo-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text-primary);
  line-height: 1.3;
  letter-spacing: 0.3px;
}

.logo-subtitle {
  font-size: 11px;
  color: var(--color-text-tertiary);
  line-height: 1.3;
}

.app-launcher-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-left: 10px;
  background: transparent;
  border: 1.5px solid transparent;
  border-radius: 10px;
  cursor: pointer;
  color: var(--color-text-tertiary);
  transition: color 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;

  &:hover {
    background: var(--color-border-light);
    color: var(--color-text-secondary);
    border-color: var(--color-border);
  }

  &--active {
    background: var(--c-accent-bg);
    color: var(--c-accent-active);
    border-color: color-mix(in oklch, var(--accent) 20%, transparent);
    box-shadow: 0 0 0 3px color-mix(in oklch, var(--accent) 8%, transparent);
  }
}

.navbar-right {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 200px;
  justify-content: flex-end;
}

.project-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px 0 8px;
  height: 32px;
  background: var(--color-bg-container);
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: color 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  margin-left: 10px;

  &:hover {
    background: var(--color-border-light);
    border-color: var(--color-border);
  }
}

.project-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
  border-radius: 4px;
  filter: grayscale(1) brightness(0.4) contrast(1.4);
}

.project-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
}

.project-dropdown-panel {
  background: var(--color-bg-elevated);
  border-radius: 12px;
  box-shadow: none;
  min-width: 280px;
  overflow: hidden;
}

.project-dropdown-header {
  padding: 12px 16px 8px;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-tertiary);
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.project-dropdown-list {
  max-height: 400px;
  overflow-y: auto;
  padding: 4px 6px 6px;
}

.project-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: color 0.15s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--color-bg-container);
  }
}

.project-dropdown-item.project-option-active {
  background: var(--c-accent-bg);
}


.project-option-icon {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  object-fit: contain;
  filter: grayscale(1) brightness(0.4) contrast(1.4);
  flex-shrink: 0;
}

.project-option-name {
  flex: 1;
  font-size: 14px;
  color: var(--color-text-primary);
  font-weight: 500;
}

.project-check {
  color: var(--color-primary);
  font-size: 14px;
  flex-shrink: 0;
}

.tool-icon {
  color: var(--color-text-tertiary);

  &:hover {
    color: var(--color-primary);
    background: var(--color-border-light);
  }
}

.console-entry-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid var(--hairline);
  background: var(--surface-2);
  color: var(--warn);
  cursor: pointer;
  transition: all 0.2s;
}
.console-entry-btn:hover {
  background: color-mix(in oklch, var(--warn) 12%, transparent);
  border-color: color-mix(in oklch, var(--warn) 40%, transparent);
  color: var(--warn);
  box-shadow: 0 0 8px color-mix(in oklch, var(--warn) 15%, transparent);
}

.command-trigger-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  width: 220px;
  height: 32px;
  background: var(--color-bg-container);
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: color 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 13px;
  color: var(--color-text-tertiary);
  font-weight: 400;
  justify-content: space-between;

  &:hover {
    background: var(--color-border-light);
    border-color: var(--color-border);
    color: var(--color-text-secondary);
  }

  &:active {
    background: var(--surface-3);
  }
}

/* 工作台切换按钮 — 与 ThemeSwitcher 风格对齐，跳转到望舒工作台 */
.workbench-switcher {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--c-border-1);
  border-radius: var(--c-radius, 8px);
  background: transparent;
  color: var(--c-text-2);
  font-family: var(--c-font-sans, 'Geist', 'Inter', sans-serif);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition:
    background-color 150ms cubic-bezier(0.4, 0, 0.2, 1),
    border-color 150ms cubic-bezier(0.4, 0, 0.2, 1),
    color 150ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--c-bg-subtle);
    border-color: var(--c-border-2);
    color: var(--c-text-1);
  }

  &:focus-visible {
    outline: 2px solid var(--c-accent-active);
    outline-offset: 2px;
  }
}

.workbench-switcher-label {
  line-height: 1;
}

.trigger-text {
  white-space: nowrap;
}

.trigger-shortcut {
  display: flex;
  align-items: center;
  gap: 3px;
  margin-left: 4px;
}

.shortcut-key {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 4px;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  color: var(--color-text-tertiary);
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.05);
}

.navbar-agent-widget {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--color-border-light);

    .navbar-robot {
      transform: scale(1.08);
    }
  }
}

// Claude 素色：navbar 机器人统一墨黑单色（无多彩 LED 风）
.navbar-robot {
  width: 24px;
  height: 22px;
  background: var(--c-text-1);
  border-radius: 6px 6px 10px 10px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  margin-top: 3px;
}

.navbar-robot-antenna {
  position: absolute;
  top: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 2px;
  height: 4px;
  background: var(--c-text-1);

  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -1.5px;
    width: 5px;
    height: 5px;
    background: var(--c-accent);
    border-radius: 50%;
    animation: antenna-blink 2s infinite;
  }
}

.navbar-robot-face {
  width: 16px;
  height: 10px;
  background: var(--c-bg-canvas);
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
}

.navbar-robot-eye {
  width: 3px;
  height: 5px;
  background: var(--c-text-1);
  border-radius: 1.5px;
  box-shadow: none;
  animation: navbar-blink 4s infinite;
}

.navbar-robot-ear {
  position: absolute;
  top: 8px;
  width: 3px;
  height: 6px;
  background: var(--c-text-1);
  border-radius: 1.5px;

  &.ear-left {
    left: -2px;
  }

  &.ear-right {
    right: -2px;
  }
}

@keyframes antenna-blink {
  0%, 90%, 100% { opacity: 1; }
  95% { opacity: 0.3; }
}

@keyframes navbar-blink {
  0%, 90%, 100% { height: 5px; }
  92% { height: 1px; }
  94% { height: 5px; }
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px 4px 4px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--color-border-light);
  }
}

.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--c-text-1);
  color: var(--surface-1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
  font-family: var(--c-font-sans);
}

.user-name {
  font-size: 13px;
  color: var(--color-text-primary);
  font-weight: 500;
}

.content-wrapper {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.main-content {
  // Claude 素色：主区用 canvas（页面底色），让里面的卡片用 surface 浮起来
  flex: 1;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  background: var(--c-bg-canvas);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;
}

@media (max-width: 768px) {
  .top-navbar {
    height: 52px;
    padding: 0 10px;
    gap: 8px;
  }

  .navbar-left {
    flex: 1 1 auto;
    overflow: hidden;
  }

  .logo-container {
    gap: 8px;
    min-width: 0;
    padding-right: 8px;
    margin-right: 0;
    border-right: 0;
  }

  .logo-icon {
    width: 28px;
    height: 28px;
  }

  .logo-text {
    min-width: 0;
  }

  .logo-title {
    max-width: 92px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
  }

  .logo-subtitle,
  .project-selector,
  .navbar-agent-widget,
  .workbench-switcher,
  .command-trigger-btn {
    display: none;
  }

  .app-launcher-btn {
    width: 34px;
    height: 34px;
    margin-left: 4px;
  }

  .navbar-right {
    flex: 0 0 auto;
    min-width: 0;
    gap: 4px;
  }

  .content-wrapper {
    min-width: 0;
  }

  .content-wrapper :deep(.governance-sidebar),
  .content-wrapper :deep(.slim-sidebar) {
    display: none;
  }

  .main-content {
    width: 100%;
    min-width: 0;
  }
}
</style>

<!-- 全局样式（用于 trigger 弹出层，因为渲染在 body 下） -->
<style lang="scss">
.app-launcher-popup {
  width: min(560px, calc(100vw - 24px));
  max-height: calc(100vh - 76px);
  padding: 0;
  box-sizing: border-box;
  background: var(--color-bg-elevated);
  border-radius: 12px;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.04),
    0 20px 60px rgba(0, 0, 0, 0.15),
    0 4px 16px rgba(0, 0, 0, 0.06);
  animation: launcher-enter 0.22s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  overflow: hidden;
  max-width: calc(100vw - 24px);

  .app-launcher-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 220px;
    gap: 12px;
    align-items: center;
    padding: 16px 18px 14px;
    border-bottom: 1px solid var(--color-border-light);
  }

  .app-launcher-heading {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 3px;
  }

  .app-launcher-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--dm-color-text);
  }

  .app-launcher-subtitle {
    color: var(--dm-color-text-muted);
    font-size: 12px;
    line-height: 1.4;
  }

  .app-launcher-search {
    width: 100%;
  }

  .app-launcher-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    padding: 10px;
    max-height: calc(100vh - 132px);
    box-sizing: border-box;
    min-width: 0;
    width: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .app-launcher-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 16px 10px 14px;
    border-radius: 8px;
    cursor: pointer;
    transition: color 0.22s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.22s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.22s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.22s cubic-bezier(0.4, 0, 0.2, 1), transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1.5px solid transparent;
    position: relative;
    text-align: center;
    min-width: 0;

    &:hover {
      background: var(--dm-color-primary-weak);
      border-color: var(--dm-color-primary-border);

      .app-card-icon {
        background: var(--dm-color-primary);
        border-color: var(--dm-color-primary);
        color: var(--dm-color-on-primary);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--dm-color-primary) 12%, transparent);
      }
    }

    &:focus-visible {
      outline: none;
      border-color: var(--dm-color-primary);
      box-shadow: var(--dm-focus-ring);
    }

    &:active {
      transform: translateY(0);
      transition-duration: 0.08s;
    }

    &--active {
      background: var(--dm-color-primary-weak);
      border-color: var(--dm-color-primary-border);

      .app-card-icon {
        background: var(--dm-color-primary);
        border-color: var(--dm-color-primary);
        color: var(--dm-color-on-primary);
      }

      .app-card-name {
        color: var(--dm-color-primary);
        font-weight: 600;
      }

      .app-card-desc {
        color: var(--dm-color-text-muted);
      }
    }

    &--selected:not(&--active) {
      background: var(--dm-color-surface-muted);
      border-color: var(--dm-color-border);
    }
  }

  .app-card-icon {
    width: 42px;
    height: 42px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--dm-color-accent);
    flex-shrink: 0;
    background: var(--dm-color-surface-muted);
    border: 1px solid var(--dm-color-border);
    box-shadow: none;
    transition:
      color 0.22s cubic-bezier(0.4, 0, 0.2, 1),
      background-color 0.22s cubic-bezier(0.4, 0, 0.2, 1),
      border-color 0.22s cubic-bezier(0.4, 0, 0.2, 1),
      box-shadow 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .app-card-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    min-width: 0;
    width: 100%;
  }

  .app-card-name {
    font-size: 13px;
    font-weight: 500;
    color: var(--dm-color-text, var(--color-text-primary));
    line-height: 1.3;
    transition: color 0.2s;
  }

  .app-card-desc {
    font-size: 11px;
    color: var(--dm-color-text-muted);
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    transition: color 0.2s;
  }

  .app-card-active-dot {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--dm-color-primary);
    box-shadow: 0 0 0 2.5px color-mix(in srgb, var(--dm-color-primary) 18%, transparent);
    animation: active-pulse 2s ease-in-out infinite;
  }

  .app-launcher-submenu {
    border-top: 1px solid var(--dm-color-border, var(--color-border-light));
    padding: 12px;
    background: var(--dm-color-bg);
  }

  .app-submenu-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 0 4px 8px;
    color: var(--dm-color-text, var(--color-text-primary));
    font-size: 13px;
    font-weight: 600;
  }

  .app-submenu-default {
    height: 26px;
    padding: 0 9px;
    border: 1px solid var(--dm-color-border);
    border-radius: 6px;
    background: var(--dm-color-surface);
    color: var(--dm-color-primary);
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
  }

  .app-submenu-default:hover {
    background: var(--dm-color-primary-weak);
    border-color: var(--dm-color-primary-border);
  }

  .app-submenu-default:focus-visible {
    outline: none;
    box-shadow: var(--dm-focus-ring);
  }

  .app-submenu-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
    max-height: min(260px, calc(100vh - 360px));
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .app-submenu-item {
    display: flex;
    min-width: 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
    padding: 9px 10px;
    border: 1px solid var(--dm-color-border);
    border-radius: 8px;
    background: var(--dm-color-surface, var(--color-bg-elevated));
    color: var(--dm-color-text, var(--color-text-primary));
    cursor: pointer;
    text-align: left;
    transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease;

    &:hover {
      background: var(--dm-color-primary-weak);
      border-color: var(--dm-color-primary-border);
    }

    &:focus-visible {
      outline: none;
      border-color: var(--dm-color-primary);
      box-shadow: var(--dm-focus-ring);
    }
  }

  .app-submenu-empty {
    grid-column: 1 / -1;
    padding: 18px;
    border: 1px dashed var(--dm-color-border);
    border-radius: 8px;
    color: var(--dm-color-text-muted);
    text-align: center;
    font-size: 12px;
  }

  .app-submenu-title {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    font-weight: 600;
  }

  .app-submenu-breadcrumb {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--color-text-tertiary);
    font-size: 11px;
  }
}

.app-launcher-trigger-content {
  max-width: calc(100vw - 24px);
}

@media (max-width: 520px) {
  .app-launcher-popup {
    border-radius: 16px;

    .app-launcher-header {
      grid-template-columns: 1fr;
    }

    .app-launcher-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .app-submenu-list {
      grid-template-columns: 1fr;
    }

    .app-launcher-card {
      padding: 14px 8px 12px;
    }
  }
}

@keyframes launcher-enter {
  from {
    opacity: 0;
    transform: scale(0.92) translateY(-6px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes active-pulse {
  0%, 100% {
    box-shadow: 0 0 0 2.5px color-mix(in srgb, var(--dm-color-primary) 16%, transparent);
  }
  50% {
    box-shadow: 0 0 0 4px color-mix(in srgb, var(--dm-color-primary) 10%, transparent);
  }
}
</style>
